import { Button, Form, Input, InputNumber, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import taskerCategoriesApi from '@/api/taskerCategories';

type LocationState = {
    edit?: boolean;
    record?: {
        taskerCategoryId: string;
        categoryName: string;
        priority: number;
    };
};

type TaskerCategoryRow = {
    taskerCategoryId: string;
    priority: number;
};

const AddTaskerCategory = () => {
    const link = useNavigate();
    const location = useLocation();
    const state = (location.state || {}) as LocationState;
    const [form] = Form.useForm();
    const [editMode, setEditMode] = useState(false);
    const [taskerCategoryId, setTaskerCategoryId] = useState('');
    const [rows, setRows] = useState<TaskerCategoryRow[]>([]);

    useEffect(() => {
        if (state.edit && state.record) {
            setEditMode(true);
            setTaskerCategoryId(state.record.taskerCategoryId);
            form.setFieldsValue({
                categoryName: state.record.categoryName,
                priority: state.record.priority,
            });
        }
    }, [state, form]);

    useEffect(() => {
        const getExistingPriorities = async () => {
            try {
                const res = await taskerCategoriesApi.getList();
                setRows(res.data || []);
            } catch (error: any) {
                message.error(error);
            }
        };

        getExistingPriorities();
    }, []);

    const validatePriority = (_: unknown, value?: number | null) => {
        if (value === undefined || value === null || Number.isNaN(Number(value))) {
            return Promise.reject(new Error('Please enter priority'));
        }
        if (Number(value) < 1) {
            return Promise.reject(new Error('Priority must be greater than 0'));
        }
        const duplicate = rows.some(
            row => row.taskerCategoryId !== taskerCategoryId && Number(row.priority) === Number(value)
        );
        if (duplicate) {
            return Promise.reject(new Error('Priority already exists'));
        }
        return Promise.resolve();
    };

    const onFinish = async (values: { categoryName: string; priority: number }) => {
        const params = {
            categoryName: values.categoryName.trim(),
            priority: values.priority,
        };
        try {
            if (editMode) {
                await taskerCategoriesApi.update(taskerCategoryId, params);
                message.success('Category updated');
            } else {
                await taskerCategoriesApi.create(params);
                message.success('Category created');
            }
            link('/tasker-categories', { replace: true });
        } catch (error: any) {
            message.error(error);
        }
    };

    return (
        <div className="page-main Categories">
            <div className="page-title">{editMode ? 'Edit tasker tier' : 'Add tasker tier'}</div>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                style={{ maxWidth: 480 }}
                initialValues={{ categoryName: '', priority: 0 }}
            >
                <Form.Item label="Name" name="categoryName" rules={[{ required: true, message: 'Please enter name' }]}>
                    <Input placeholder="Category name" maxLength={128} />
                </Form.Item>
                <Form.Item
                    label="Priority"
                    name="priority"
                    rules={[{ required: true, message: 'Please enter priority' }, { validator: validatePriority }]}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        precision={0}
                        placeholder="Number (e.g. lower = higher rank)"
                    />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit" style={{ marginRight: 12 }}>
                        Save
                    </Button>
                    <Button onClick={() => link(-1)}>Cancel</Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default AddTaskerCategory;
